<template>
  <div class="game-parameters-form">
    <!-- 標題（自製遊戲不顯示） -->
    <div class="parameters-header" v-if="gameType !== 'aigame'">
      <label class="setting-label">{{ t('gameActManager.gameParameters') }} - {{ gameTypeLabel }}</label>
      <button class="clear-settings-btn" @click="handleClearSettings">
        {{ t('gameActManager.clearSettings') }}
      </button>
    </div>

    <!-- 打地鼠設定 -->
    <div v-if="gameType === 'mole'" class="game-settings-content">
      <div class="setting-section">
        <div class="setting-title">{{ t('gameActManager.topicWithActivityName') }}</div>
        <div class="vocabulary-counter">
          {{ formData.topic?.length || 0 }}/20
        </div>
        <input
          class="input-text"
          type="text"
          v-model="formData.topic"
          :placeholder="t('gameActManager.inputTopic')"
          maxlength="20"
        >
      </div>

      <div class="setting-section correct-section">
        <div class="setting-title">{{ t('gameActManager.correctItems') }}</div>
        <div class="vocabulary-counter">
          {{ formData.vocabularyList?.length || 0 }}/20
        </div>
        <div class="vocabulary-input">
          <textarea
            v-model="vocabularyInput"
            :placeholder="t('gameActManager.correctItemsPlaceholder')"
            @keydown="handleKeyDown"
          ></textarea>
        </div>

        <div class="vocabulary-tags">
          <div v-for="(word, index) in formData.vocabularyList" :key="index" class="vocabulary-tag">
            {{ word }}
            <span class="delete-tag" @click="removeVocabulary(index)">×</span>
          </div>
        </div>
      </div>

      <div class="setting-section error-section">
        <div class="setting-title">{{ t('gameActManager.errorItems') }}</div>
        <div class="vocabulary-counter">
          {{ formData.errorVocabularyList?.length || 0 }}/20
        </div>
        <div class="vocabulary-input">
          <textarea
            v-model="errorVocabularyInput"
            :placeholder="t('gameActManager.errorItemsPlaceholder')"
            @keydown="handleErrorKeyDown"
          ></textarea>
        </div>

        <div class="vocabulary-tags error-tags">
          <div v-for="(word, index) in formData.errorVocabularyList" :key="index" class="vocabulary-tag error-tag">
            {{ word }}
            <span class="delete-tag" @click="removeErrorVocabulary(index)">×</span>
          </div>
        </div>
      </div>

      <div class="setting-section">
        <div class="setting-title">{{ t('gameActManager.gameDuration') }}</div>
        <div class="duration-selector">
          <select v-model="formData.gameDuration">
            <option v-for="option in durationOptions" :key="option" :value="option">
              {{ option }} {{ t('gameActManager.second') }}
            </option>
          </select>
        </div>
      </div>

      <div class="setting-section">
        <div class="setting-title">{{ t('gameActManager.moleSpeed') }}</div>
        <div class="speed-selector">
          <select v-model="formData.moleSpeed">
            <option v-for="option in moleSpeedOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>

      <div class="setting-section">
        <div class="setting-title">{{ t('gameActManager.backgroundSetting') }}</div>
        <div class="background-selector">
          <div
            v-for="option in moleBackgroundData"
            :key="option.value"
            class="background-option"
            :class="{ 'selected': formData.background === option.value }"
            @click="formData.background = option.value"
          >
            <img :src="option.image" />
            <div class="background-label">{{ t('gameActManager.' + option.value) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 火箭升空（是非題）設定 -->
    <div v-if="gameType === 'tf'" class="game-settings-content">
      <div class="setting-section">
        <div class="setting-title">{{ t('gameActManager.topicWithActivityName') }}</div>
        <div class="vocabulary-counter">
          {{ formData.topic?.length || 0 }}/20
        </div>
        <input
          class="input-text"
          type="text"
          v-model="formData.topic"
          :placeholder="t('gameActManager.inputTopic')"
          maxlength="20"
        >
      </div>

      <div class="setting-section correct-section">
        <div class="setting-title">{{ t('PvPTFgame.questionLabel') }}</div>
        <div class="vocabulary-counter">
          {{ formData.questionList?.length || 0 }}/20
        </div>

        <div v-for="(question, index) in formData.questionList" :key="index" class="question-item">
          <div class="question-content">
            <div class="question-text">{{ question.text }}</div>
            <div class="question-answer" :class="{ 'true': question.isTrue, 'false': !question.isTrue }">
              <svg v-if="question.isTrue" class="icon-svg correct" viewBox="0 0 341.333 341.333">
                <path d="M170.667,0C76.41,0,0,76.41,0,170.667s76.41,170.667,170.667,170.667s170.667-76.41,170.667-170.667S264.923,0,170.667,0z
                  M170.667,298.667c-70.692,0-128-57.308-128-128s57.308-128,128-128s128,57.308,128,128S241.359,298.667,170.667,298.667z"/>
              </svg>
              <svg v-else class="icon-svg wrong" viewBox="0 0 1024 1024">
                <path d="M195.2 195.2a64 64 0 0 1 90.496 0L512 421.504 738.304 195.2a64 64 0 0 1 90.496 90.496L602.496 512 828.8 738.304a64 64 0 0 1-90.496 90.496L512 602.496 285.696 828.8a64 64 0 0 1-90.496-90.496L421.504 512 195.2 285.696a64 64 0 0 1 0-90.496z"/>
              </svg>
            </div>
          </div>
          <button class="icon-button delete" @click="removeQuestion(index)">
            <svg class="icon-svg" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a1 1 0 01-1 1H3a1 1 0 110-2h14a1 1 0 011 1z"/>
            </svg>
          </button>
        </div>

        <div class="question-input">
          <input class="input-text" type="text" v-model="questionInput" :placeholder="t('PvPTFgame.inputQuestion')">
          <div class="answer-selector">
            <div
              class="answer-option"
              :class="{ 'selected': newQuestionIsTrue }"
              @click="newQuestionIsTrue = true"
            >
              <svg class="icon-svg" viewBox="0 0 341.333 341.333">
                <path d="M170.667,0C76.41,0,0,76.41,0,170.667s76.41,170.667,170.667,170.667s170.667-76.41,170.667-170.667S264.923,0,170.667,0z
                  M170.667,298.667c-70.692,0-128-57.308-128-128s57.308-128,128-128s128,57.308,128,128S241.359,298.667,170.667,298.667z"/>
              </svg>
            </div>
            <div
              class="answer-option"
              :class="{ 'selected': !newQuestionIsTrue }"
              @click="newQuestionIsTrue = false"
            >
              <svg class="icon-svg" viewBox="0 0 1024 1024">
                <path d="M195.2 195.2a64 64 0 0 1 90.496 0L512 421.504 738.304 195.2a64 64 0 0 1 90.496 90.496L602.496 512 828.8 738.304a64 64 0 0 1-90.496 90.496L512 602.496 285.696 828.8a64 64 0 0 1-90.496-90.496L421.504 512 195.2 285.696a64 64 0 0 1 0-90.496z"/>
              </svg>
            </div>
          </div>
          <button class="add-button" @click="addQuestion" :disabled="!questionInput.trim()">
            <svg class="icon-svg" viewBox="0 0 24 24">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M13 6C13 5.44771 12.5523 5 12 5C11.4477 5 11 5.44771 11 6V11H6C5.44771 11 5 11.4477 5 12C5 12.5523 5.44771 13 6 13H11V18C11 18.5523 11.4477 19 12 19C12.5523 19 13 18.5523 13 18V13H18C18.5523 13 19 12.5523 19 12C19 11.4477 18.5523 11 18 11H13V6Z" />
            </svg>
          </button>
        </div>
      </div>

      <div class="setting-section">
        <div class="setting-title">{{ t('PvPTFgame.gameDuration') }}</div>
        <div class="duration-selector">
          <select v-model="formData.gameDuration">
            <option v-for="option in tfDurationOptions" :key="option" :value="option">
              {{ option }} {{ t('gameActManager.second') }}
            </option>
          </select>
        </div>
      </div>

      <div class="setting-section" v-if="playerCount === 2">
        <div class="setting-title">{{ t('PvPTFgame.answerMode') }}</div>
        <div class="answer-mode-selector">
          <label class="radio-option">
            <input type="radio" v-model="formData.answerMode" value="sync" name="answerMode">
            <span class="radio-label">{{ t('PvPTFgame.syncMode') }}</span>
          </label>
          <label class="radio-option">
            <input type="radio" v-model="formData.answerMode" value="individual" name="answerMode">
            <span class="radio-label">{{ t('PvPTFgame.individualMode') }}</span>
          </label>
        </div>
      </div>
    </div>

    <!-- 🐍 貪食蛇設定 (使用獨立元件) -->
    <HTSnakeGameSetting
      ref="snakeGameSettingRef"
      v-if="gameType === 'snake'"
      :modelValue="formData"
      @update:modelValue="handleSnakeUpdate"
      :activity-name="props.activityName"
      :player-count="props.playerCount"
      :is-embedded="true"
    />

    <!-- 🧩 迷宮設定 (使用獨立元件) -->
    <HTMazeGameSetting
      ref="mazeGameSettingRef"
      v-if="gameType === 'maze'"
      :modelValue="formData"
      @update:modelValue="handleMazeUpdate"
      :activity-name="props.activityName"
      :player-count="props.playerCount"
      :is-embedded="true"
    />

    <!-- 🎨 自製遊戲設定 -->
    <div v-if="gameType === 'aigame'" class="game-settings-content aigame-content">
      <div class="code-input-container">
        <div class="code-editor-wrapper">
          <pre class="code-highlight"><code class="language-markup" v-html="highlightedCode"></code></pre>
          <textarea
            v-model="formData.customCode"
            @input="updateHighlight"
            @scroll="syncScroll"
            :placeholder="t('gameActManager.customGameCodePlaceholder')"
            class="code-textarea"
            rows="20"
            spellcheck="false"
            ref="codeTextarea"
          ></textarea>
        </div>
      </div>
    </div>

    <!-- 摘星星設定 -->
    <div v-if="gameType === 'star'" class="game-settings-content">
      <div class="setting-section">
        <div class="setting-title">{{ t('gameActManager.topicWithActivityName') }}</div>
        <div class="vocabulary-counter">
          {{ formData.topic?.length || 0 }}/20
        </div>
        <input
          class="input-text"
          type="text"
          v-model="formData.topic"
          :placeholder="t('gameActManager.inputTopic')"
          maxlength="20"
        >
      </div>

      <div class="setting-section correct-section">
        <div class="setting-title">{{ t('gameActManager.correctItems') }}</div>
        <div class="vocabulary-counter">
          {{ formData.vocabularyList?.length || 0 }}/20
        </div>
        <div class="vocabulary-input">
          <textarea
            v-model="vocabularyInput"
            :placeholder="t('gameActManager.correctItemsPlaceholder')"
            @keydown="handleKeyDown"
          ></textarea>
        </div>

        <div class="vocabulary-tags">
          <div v-for="(word, index) in formData.vocabularyList" :key="index" class="vocabulary-tag">
            {{ word }}
            <span class="delete-tag" @click="removeVocabulary(index)">×</span>
          </div>
        </div>
      </div>

      <div class="setting-section error-section">
        <div class="setting-title">{{ t('gameActManager.errorItems') }}</div>
        <div class="vocabulary-counter">
          {{ formData.errorVocabularyList?.length || 0 }}/20
        </div>
        <div class="vocabulary-input">
          <textarea
            v-model="errorVocabularyInput"
            :placeholder="t('gameActManager.errorItemsPlaceholder')"
            @keydown="handleErrorKeyDown"
          ></textarea>
        </div>

        <div class="vocabulary-tags error-tags">
          <div v-for="(word, index) in formData.errorVocabularyList" :key="index" class="vocabulary-tag error-tag">
            {{ word }}
            <span class="delete-tag" @click="removeErrorVocabulary(index)">×</span>
          </div>
        </div>
      </div>

      <div class="setting-section">
        <div class="setting-title">{{ t('gameActManager.gameDuration') }}</div>
        <div class="duration-selector">
          <select v-model="formData.gameDuration">
            <option v-for="option in durationOptions" :key="option" :value="option">
              {{ option }} {{ t('gameActManager.second') }}
            </option>
          </select>
        </div>
      </div>

      <div class="setting-section">
        <div class="setting-title">{{ t('gameActManager.gameSpeed') }}</div>
        <div class="duration-selector">
          <select v-model="formData.gameSpeed">
            <option v-for="option in starSpeedOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>

      <div class="setting-section">
        <div class="setting-title">{{ t('gameActManager.backgroundSetting') }}</div>
        <div class="background-selector">
          <div
            v-for="option in starBackgroundData"
            :key="option.value"
            class="background-option"
            :class="{ 'selected': formData.background === option.value }"
            @click="formData.background = option.value"
          >
            <img :src="option.image" />
            <div class="background-label">{{ t('gameActManager.' + option.value) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 清空設定確認 Modal -->
    <div v-if="showClearConfirmModal" class="modal-overlay" @click="showClearConfirmModal = false">
      <div class="modal-content clear-confirm-modal" @click.stop>
        <div class="modal-header">
          <h3>{{ t('gameActManager.clearSettings') }}</h3>
        </div>
        <div class="modal-body">
          <p>{{ t('gameActManager.confirmClearSettings') }}</p>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="showClearConfirmModal = false">
            {{ t('gameActManager.cancel') }}
          </button>
          <button class="confirm-btn danger-btn" @click="confirmClearSettings">
            {{ t('gameActManager.confirm') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, reactive, watch, computed, inject, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import HTSnakeGameSetting from '@/HTwbActKit/HTSnakeGameSetting.vue';
import HTMazeGameSetting from '@/HTwbActKit/HTMazeGameSetting.vue';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-javascript';

const { t } = useI18n();
const $Message = inject('$Message');

// 代碼編輯器相關
const codeTextarea = ref(null);
const highlightedCode = ref('');

// Props
const props = defineProps({
  gameType: {
    type: String, // 'mole', 'star', 或 'tf'
    required: true
  },
  activityName: {
    type: String,
    required: false,
    default: ''
  },
  playerCount: {
    type: Number,
    required: false,
    default: 1
  },
  modelValue: {
    type: Object,
    default: () => ({})
  }
});

// Emits
const emit = defineEmits(['update:modelValue']);

// 根據遊戲類型初始化預設值
const getInitialFormData = () => {
  const topicValue = props.activityName || 'Choose Fruits (Demo)';

  if (props.gameType === 'tf') {
    // 火箭升空（是非題）預設值
    return {
      topic: topicValue,
      questionList: [
        { text: 'apple', isTrue: true },
        { text: 'banana', isTrue: true },
        { text: 'orange', isTrue: true },
        { text: 'grape', isTrue: true },
        { text: 'watermelon', isTrue: true },
        { text: 'strawberry', isTrue: true },
        { text: 'blueberry', isTrue: true },
        { text: 'pineapple', isTrue: true },
        { text: 'kiwi', isTrue: false },
        { text: 'mango', isTrue: false }
      ],
      gameDuration: 10,
      answerMode: 'sync',
      vocabularyList: [],
      errorVocabularyList: []
    };
  } else if (props.gameType === 'mole') {
    // 打地鼠預設值
    return {
      topic: topicValue,
      vocabularyList: ['apple', 'banana', 'orange', 'grape', 'watermelon', 'strawberry', 'blueberry', 'pineapple', 'kiwi', 'mango'],
      errorVocabularyList: ['pen', 'pencil', 'notebook', 'eraser', 'ruler', 'scissors', 'calculator', 'backpack', 'paper', 'stapler'],
      gameDuration: 30,
      background: 'forest',
      moleSpeed: 2500,
      questionList: [],
      answerMode: 'sync'
    };
  } else if (props.gameType === 'star') {
    // 摘星星預設值
    return {
      topic: topicValue,
      vocabularyList: ['apple', 'banana', 'orange', 'grape', 'watermelon', 'strawberry', 'blueberry', 'pineapple', 'kiwi', 'mango'],
      errorVocabularyList: ['pen', 'pencil', 'notebook', 'note', 'notebook', 'notebook', 'notebook', 'notebook', 'notebook', 'notebook'],
      gameDuration: 40,
      background: 'desert',
      gameSpeed: 2,
      questionList: [],
      answerMode: 'sync'
    };
  } else if (props.gameType === 'snake') {
    // 🐍 貪食蛇預設值
    return {
      topic: topicValue,
      vocabularyList: ['apple', 'banana', 'orange', 'grape', 'watermelon'],
      errorVocabularyList: ['pen', 'pencil', 'notebook', 'eraser', 'ruler'],
      difficulty: 'medium',
      gameDuration: 60,
      boardSize: 20,
      snakeSpeed: 200,
      foodCount: 8,
      initialSnakeLength: 3,
      questionList: [],
      answerMode: 'sync'
    };
  } else if (props.gameType === 'maze') {
    // 🧩 迷宮預設值
    return {
      topic: topicValue,
      questionList: [
        {
          text: 'What is the capital of France?',
          options: [
            { text: 'Paris', isCorrect: true },
            { text: 'London', isCorrect: false },
            { text: 'Berlin', isCorrect: false },
            { text: 'Madrid', isCorrect: false }
          ]
        },
        {
          text: 'Which planet is known as the Red Planet?',
          options: [
            { text: 'Mars', isCorrect: true },
            { text: 'Venus', isCorrect: false },
            { text: 'Jupiter', isCorrect: false }
          ]
        }
      ]
    };
  } else if (props.gameType === 'aigame') {
    // 🎨 自製遊戲預設值（完全自由的 HTML/CSS/JavaScript）
    return {
      customCode: '' // 使用者自定義的完整遊戲代碼
    };
  }

  return {
    topic: '',
    vocabularyList: [],
    errorVocabularyList: [],
    gameDuration: 30,
    questionList: [],
    answerMode: 'sync'
  };
};

// 表單資料（如果有 modelValue 且有實際內容則使用，否則使用預設值）
const formData = reactive(
  props.modelValue && Object.keys(props.modelValue).length > 0
    ? { ...getInitialFormData(), ...props.modelValue } // 先載入預設值，再用 modelValue 覆蓋
    : getInitialFormData()
);

// 輸入框
const vocabularyInput = ref('');
const errorVocabularyInput = ref('');

// 子組件引用
const snakeGameSettingRef = ref(null);
const mazeGameSettingRef = ref(null);

// 是非題相關
const questionInput = ref('');
const newQuestionIsTrue = ref(true);

// 清空設定確認 Modal
const showClearConfirmModal = ref(false);

// 遊戲時長選項
const durationOptions = [20, 30, 40];

// 火箭升空時長選項（參照 HTpvpTFGameSetting.vue）
const tfDurationOptions = [7, 10, 15];

// 打地鼠速度選項
const moleSpeedOptions = computed(() => [
  { value: 2500, label: t('gameActManager.speedSlow') },
  { value: 2000, label: t('gameActManager.speedMedium') },
  { value: 1500, label: t('gameActManager.speedFast') },
  { value: 1000, label: t('gameActManager.speedVeryFast') }
]);

// 摘星星速度選項
const starSpeedOptions = computed(() => [
  { value: 1, label: t('gameActManager.speedSlow') },
  { value: 2, label: t('gameActManager.speedMedium') },
  { value: 3, label: t('gameActManager.speedFast') }
]);

// 遊戲類型標籤
const gameTypeLabel = computed(() => {
  if (props.gameType === 'mole') return t('gameActManager.moleGame');
  if (props.gameType === 'star') return t('gameActManager.starGame');
  if (props.gameType === 'snake') return t('gameActManager.snakeGame');
  if (props.gameType === 'tf') return t('gameActManager.rocketGame');
  if (props.gameType === 'maze') return t('gameActManager.mazeGame');
  if (props.gameType === 'aigame') return t('gameActManager.aiGame');
  return '';
});

// 打地鼠背景資料
const moleBackgroundData = [
  { value: 'forest', image: require('@/assets/HTwbActAssets/MolegameObj/mole-forest.webp') },
  { value: 'desert', image: require('@/assets/HTwbActAssets/MolegameObj/mole-desert.webp') }
];

// 摘星星背景資料
const starBackgroundData = [
  { value: 'ocean', image: require('@/assets/HTwbActAssets/ocean_world.webp') },
  { value: 'desert', image: require('@/assets/HTwbActAssets/desert_oasis.webp') },
  { value: 'forest', image: require('@/assets/HTwbActAssets/fantasy_forest.webp') }
];

// 初始化時立即向父組件更新（確保 gameParameters 有值）
emit('update:modelValue', { ...formData });

// 切換遊戲類型時重置為該類型的預設值
watch(() => props.gameType, (newType, oldType) => {
  // 只有當遊戲類型真正改變時才重置（避免初始化時重複執行）
  if (newType !== oldType && oldType !== undefined) {
    const newData = getInitialFormData();
    Object.assign(formData, newData);
    emit('update:modelValue', { ...formData });
  }
});

// 監聽變化並向上傳遞
watch(formData, (newVal) => {
  emit('update:modelValue', { ...newVal });
}, { deep: true });

// 監聽活動名稱變化，同步到主題欄位
watch(() => props.activityName, (newName) => {
  if (newName && newName.trim() !== '') {
    formData.topic = newName;
  }
});

// 處理正確詞彙的按鍵事件
const handleKeyDown = (event) => {
  // 移除紅框 (如果使用者開始編輯)
  const textarea = event.target;
  if (textarea.style.border === '2px solid red') {
    textarea.style.border = '';
  }

  if (event.key === 'Enter') {
    event.preventDefault();
    addVocabulary();
  }
};

// 處理錯誤詞彙的按鍵事件
const handleErrorKeyDown = (event) => {
  // 移除紅框 (如果使用者開始編輯)
  const textarea = event.target;
  if (textarea.style.border === '2px solid red') {
    textarea.style.border = '';
  }

  if (event.key === 'Enter') {
    event.preventDefault();
    addErrorVocabulary();
  }
};

// 新增正確詞彙
const addVocabulary = () => {
  const words = vocabularyInput.value.split(/[,，\n\r]+/).filter(word => word.trim() !== '');

  for (const word of words) {
    if (formData.vocabularyList.includes(word.trim())) {
      $Message.warning({
        content: t('gameActManager.wordExists'),
        duration: 0.5,
      });
    }
    if (formData.vocabularyList.length < 20 && !formData.vocabularyList.includes(word.trim())) {
      formData.vocabularyList.push(word.trim());
    }
  }

  if (formData.vocabularyList.length === 20) {
    $Message.warning({
      content: t('gameActManager.maxVocabulary'),
      duration: 0.5,
    });
  }

  vocabularyInput.value = '';
};

// 新增錯誤詞彙
const addErrorVocabulary = () => {
  const words = errorVocabularyInput.value.split(/[,，\n\r]+/).filter(word => word.trim() !== '');

  for (const word of words) {
    if (formData.errorVocabularyList.includes(word.trim())) {
      $Message.warning({
        content: t('gameActManager.wordExists'),
        duration: 0.5,
      });
    }
    if (formData.errorVocabularyList.length < 20 && !formData.errorVocabularyList.includes(word.trim())) {
      formData.errorVocabularyList.push(word.trim());
    }
  }

  if (formData.errorVocabularyList.length === 20) {
    $Message.warning({
      content: t('gameActManager.maxErrorVocabulary'),
      duration: 0.5,
    });
  }

  errorVocabularyInput.value = '';
};

// 移除正確詞彙
const removeVocabulary = (index) => {
  formData.vocabularyList.splice(index, 1);
};

// 移除錯誤詞彙
const removeErrorVocabulary = (index) => {
  formData.errorVocabularyList.splice(index, 1);
};

// 新增是非題題目（參照 HTpvpTFGameSetting.vue）
const addQuestion = () => {
  if (questionInput.value.trim() === '') {
    return;
  }

  if (formData.questionList.length >= 20) {
    $Message.warning({
      content: t('PvPTFgame.maxQuestions'),
      duration: 0.5,
    });
    return;
  }

  // 檢查是否有重複題目
  const isDuplicate = formData.questionList.some(q => q.text === questionInput.value.trim());
  if (isDuplicate) {
    $Message.warning({
      content: t('PvPTFgame.questionExists'),
      duration: 0.5,
    });
    return;
  }

  // 添加新題目
  formData.questionList = [...formData.questionList, {
    text: questionInput.value.trim(),
    isTrue: newQuestionIsTrue.value
  }];

  // 清空輸入框
  questionInput.value = '';
};

// 移除是非題題目
const removeQuestion = (index) => {
  formData.questionList = formData.questionList.filter((_, i) => i !== index);
};

// 🐍 處理貪食蛇設定更新
const handleSnakeUpdate = (newData) => {
  console.log('🐍 [GameParametersForm] handleSnakeUpdate 收到:', {
    difficulty: newData.difficulty,
    boardSize: newData.boardSize,
    snakeSpeed: newData.snakeSpeed,
    foodCount: newData.foodCount
  });

  // 使用 Object.assign 更新 formData,確保觸發 watch
  Object.assign(formData, newData);
};

// 🧩 處理迷宮設定更新
const handleMazeUpdate = (newData) => {
  console.log('🧩 [GameParametersForm] handleMazeUpdate 收到:', {
    topic: newData.topic,
    questionCount: newData.questionList?.length || 0
  });

  // 使用 Object.assign 更新 formData,確保觸發 watch
  Object.assign(formData, newData);
};

// 驗證輸入框是否有未提交的內容
const validateInputs = () => {
  // 打地鼠或摘星星遊戲才有詞彙輸入框
  if (props.gameType === 'mole' || props.gameType === 'star') {
    const hasUnsubmittedCorrect = vocabularyInput.value.trim() !== '';
    const hasUnsubmittedError = errorVocabularyInput.value.trim() !== '';

    if (hasUnsubmittedCorrect || hasUnsubmittedError) {
      // 為有內容的輸入框添加紅框
      const correctTextarea = document.querySelector('.correct-section textarea');
      const errorTextarea = document.querySelector('.error-section textarea');

      if (hasUnsubmittedCorrect && correctTextarea) {
        correctTextarea.style.border = '2px solid red';
      }
      if (hasUnsubmittedError && errorTextarea) {
        errorTextarea.style.border = '2px solid red';
      }

      // 顯示提示訊息
      $Message.warning({
        content: t('gameActManager.unsubmittedWarning'),
        duration: 1,
      });

      return false; // 驗證失敗
    }
  }

  // 貪食蛇遊戲使用獨立組件,調用其驗證方法
  if (props.gameType === 'snake' && snakeGameSettingRef.value) {
    return snakeGameSettingRef.value.validateInputs();
  }

  return true; // 驗證成功
};

// 驗證必填欄位
const validateRequiredFields = () => {
  const errors = [];

  if (props.gameType === 'mole' || props.gameType === 'star') {
    // 打地鼠和摘星星：檢查主題、答對題目、答錯題目
    if (!formData.topic || formData.topic.trim() === '') {
      errors.push(t('gameActManager.topicRequired'));
    }
    if (!formData.vocabularyList || formData.vocabularyList.length === 0) {
      errors.push(t('gameActManager.correctItemsRequired'));
    }
    if (!formData.errorVocabularyList || formData.errorVocabularyList.length === 0) {
      errors.push(t('gameActManager.errorItemsRequired'));
    }
  } else if (props.gameType === 'tf') {
    // 火箭升空：檢查主題和題目列表
    if (!formData.topic || formData.topic.trim() === '') {
      errors.push(t('gameActManager.topicRequired'));
    }
    if (!formData.questionList || formData.questionList.length === 0) {
      errors.push(t('gameActManager.questionListRequired'));
    }
  } else if (props.gameType === 'snake') {
    // 貪食蛇：檢查主題、答對題目、答錯題目
    if (!formData.topic || formData.topic.trim() === '') {
      errors.push(t('gameActManager.topicRequired'));
    }
    if (!formData.vocabularyList || formData.vocabularyList.length === 0) {
      errors.push(t('gameActManager.correctItemsRequired'));
    }
    if (!formData.errorVocabularyList || formData.errorVocabularyList.length === 0) {
      errors.push(t('gameActManager.errorItemsRequired'));
    }
  } else if (props.gameType === 'maze') {
    // 迷宮：檢查主題和題目列表
    if (!formData.topic || formData.topic.trim() === '') {
      errors.push(t('gameActManager.topicRequired'));
    }
    if (!formData.questionList || formData.questionList.length === 0) {
      errors.push(t('gameActManager.questionListRequired'));
    }
  }

  if (errors.length > 0) {
    $Message.error({
      content: t('gameActManager.requiredFieldsMissing') + '\n' + errors.join('\n'),
      duration: 2
    });
    return false;
  }

  return true;
};

// 打開清空設定確認 Modal
const handleClearSettings = () => {
  showClearConfirmModal.value = true;
};

// 確認清空設定（真正清空所有欄位）
const confirmClearSettings = () => {
  console.log('[清空設定] 開始執行，遊戲類型:', props.gameType);

  // 清空輸入框
  vocabularyInput.value = '';
  errorVocabularyInput.value = '';
  questionInput.value = '';

  // 如果是貪食蛇遊戲，調用子組件的清空方法
  if (props.gameType === 'snake' && snakeGameSettingRef.value) {
    snakeGameSettingRef.value.clearData();
  }
  // 如果是迷宮遊戲，調用子組件的清空方法
  else if (props.gameType === 'maze' && mazeGameSettingRef.value) {
    mazeGameSettingRef.value.clearData();
  }
  // 其他遊戲直接清空 formData
  else {
    // 建立空白模板（保留基本設定，清空內容欄位）
    const emptyTemplate = {
      topic: '',
      vocabularyList: [],
      errorVocabularyList: [],
      questionList: [],
      customCode: '',
      // 保留非內容的設定值
      gameDuration: formData.gameDuration || 30,
      background: formData.background,
      moleSpeed: formData.moleSpeed,
      gameSpeed: formData.gameSpeed,
      answerMode: formData.answerMode || 'sync'
    };

    // 使用 Object.assign 整體替換 formData
    Object.assign(formData, emptyTemplate);
  }

  console.log('[清空設定] 清空後 formData:', {
    topic: formData.topic,
    vocabularyListLength: formData.vocabularyList?.length,
    errorVocabularyListLength: formData.errorVocabularyList?.length,
    questionListLength: formData.questionList?.length
  });

  // 關閉 Modal
  showClearConfirmModal.value = false;

  $Message.success({
    content: t('gameActManager.settingsCleared'),
    duration: 0.5
  });
};

// 代碼高亮更新
const updateHighlight = () => {
  const code = formData.customCode || '';
  if (code) {
    highlightedCode.value = Prism.highlight(code, Prism.languages.markup, 'markup');
  } else {
    highlightedCode.value = '';
  }
};

// 同步滾動
const syncScroll = (event) => {
  const target = event.target;
  const pre = target.previousElementSibling;
  if (pre) {
    pre.scrollTop = target.scrollTop;
    pre.scrollLeft = target.scrollLeft;
  }
};

// 監聽自定義代碼變化，更新高亮
watch(() => formData.customCode, () => {
  if (props.gameType === 'aigame') {
    nextTick(() => {
      updateHighlight();
    });
  }
}, { immediate: true });

// 組件掛載時初始化高亮
onMounted(() => {
  if (props.gameType === 'aigame') {
    updateHighlight();
  }
});

// 暴露驗證方法給父組件
defineExpose({
  validateInputs,
  validateRequiredFields
});
</script>

<style lang="less" scoped>
@import "../../assets/color.less";
@import "../../assets/gameActManager.less";

.game-parameters-form {
  width: 100%;
}

.parameters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  .setting-label {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 0;
    line-height: 1.6;
  }

  .clear-settings-btn {
    padding: 6px 16px;
    background-color: #ff4d4f;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.2s;

    &:hover {
      background-color: #ff7875;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(255, 77, 79, 0.3);
    }

    &:active {
      transform: translateY(0);
    }
  }
}

.game-settings-content {
  width: 100%;
}

.setting-section {
  position: relative;
  margin-bottom: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 16px;

  .setting-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 10px;
    line-height: 1.6;
  }

  .input-text {
    width: 100%;
    height: 40px;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.6;
  }

  &.correct-section {
    background-color: #e8f4ff;

    .setting-title {
      color: blue;
    }
  }

  &.error-section {
    background-color: #fff0f3;

    .setting-title {
      color: #d81b60;
    }
  }
}

.vocabulary-input {
  textarea {
    width: 100%;
    height: 80px;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    resize: vertical;
    font-family: inherit;
    font-size: 14px;
    margin-bottom: 10px;
    line-height: 1.6;
  }
}

.vocabulary-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .vocabulary-tag {
    background-color: #e8f4ff;
    border: 1px solid #b8dcff;
    border-radius: 20px;
    padding: 4px 10px;
    display: flex;
    align-items: center;
    font-size: 14px;
    line-height: 1.6;

    .delete-tag {
      margin-left: 5px;
      cursor: pointer;
      font-weight: bold;
      color: #666;

      &:hover {
        color: #ff4d4d;
      }
    }

    &.error-tag {
      background-color: #ffe0e9;
      border: 1px solid #ffb6c9;

      .delete-tag {
        &:hover {
          color: #d81b60;
        }
      }
    }
  }
}

.vocabulary-counter {
  position: absolute;
  right: 18px;
  top: 18px;
  font-size: 14px;
  color: #666;
  font-weight: bold;
}

.duration-selector, .speed-selector {
  select {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.6;
  }
}

.background-selector {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 10px;

  .background-option {
    width: 120px;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    border: 3px solid transparent;

    img {
      width: 100%;
      height: 80px;
      object-fit: cover;
      display: block;
    }

    .background-label {
      padding: 6px;
      text-align: center;
      background-color: #f5f5f5;
      font-size: 14px;
      line-height: 1.6;
    }

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    &.selected {
      border-color: @game-primary-color;

      &::after {
        content: '✓';
        position: absolute;
        top: 5px;
        right: 5px;
        width: 22px;
        height: 22px;
        background-color: @game-primary-color;
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 14px;
      }

      .background-label {
        background-color: @game-active-color;
        color: @game-primary-color;
        font-weight: bold;
      }
    }
  }
}

// 火箭升空（是非題）相關樣式（參照 HTpvpTFGameSetting.vue）
.question-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: white;
  padding: 10px 15px;
  border-radius: 4px;
  margin-bottom: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.question-content {
  display: flex;
  align-items: center;
  flex: 1;
}

.question-text {
  flex: 1;
  margin-right: 10px;
  font-size: 14px;
  line-height: 1.6;
}

.question-answer {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px 8px;
  border-radius: 4px;
  width: 30px;
  height: 30px;

  &.true {
    background-color: #e6f7e6;
  }

  &.false {
    background-color: #f8d7da;
  }
}

.icon-svg {
  width: 20px;
  height: 20px;

  &.correct {
    fill: #28a745;
  }

  &.wrong {
    fill: #dc3545;
  }
}

.icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }

  &.delete {
    margin-left: 10px;

    .icon-svg {
      fill: #dc3545;
    }
  }
}

.question-input {
  display: flex;
  flex-direction: column;
  background-color: white;
  padding: 15px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.answer-selector {
  display: flex;
  margin: 10px 0;
}

.answer-option {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 8px;
  border: 1px solid #ddd;
  cursor: pointer;
  transition: all 0.2s;
  height: 40px;

  &:first-child {
    border-radius: 4px 0 0 4px;
  }

  &:last-child {
    border-radius: 0 4px 4px 0;
  }

  .icon-svg {
    fill: #6c757d;
  }

  &.selected {
    background-color: #007bff;
    border-color: #007bff;

    .icon-svg {
      fill: white;
    }
  }
}

.add-button {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.2s;
  height: 44px;

  .icon-svg {
    fill: white;
    width: 24px;
    height: 24px;
  }

  &:hover {
    background-color: #218838;
  }

  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
}

.answer-mode-selector {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.radio-option {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f8f9fa;
  }

  input[type="radio"] {
    margin-right: 8px;
    accent-color: @game-primary-color;
  }

  .radio-label {
    font-size: 16px;
    user-select: none;
    line-height: 1.6;
  }
}

// 自製遊戲樣式
.aigame-content {
  padding: 0;
  background-color: transparent;
  height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
}

.code-input-container {
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;

  .code-editor-wrapper {
    position: relative;
    width: 100%;
    flex: 1;
    min-height: 0;
    border-radius: 8px;
    overflow: hidden;
  }

  .code-highlight {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 16px;
    font-family: 'Fira Code', 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
    font-size: 14px;
    line-height: 1.6;
    background-color: #282c34;
    border: 1px solid #3e4451;
    border-radius: 8px;
    overflow: auto;
    pointer-events: none;
    white-space: pre;

    code {
      font-family: inherit;
      font-size: inherit;
      line-height: inherit;
      white-space: pre;
    }

    &::-webkit-scrollbar {
      width: 10px;
      height: 10px;
    }

    &::-webkit-scrollbar-track {
      background: #21252b;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #4b5263;
      border-radius: 4px;

      &:hover {
        background: #5c6370;
      }
    }

    &::-webkit-scrollbar-corner {
      background: #21252b;
    }
  }

  .code-textarea {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 16px;
    font-family: 'Fira Code', 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
    font-size: 14px;
    line-height: 1.6;
    color: transparent;
    caret-color: #528bff;
    background-color: transparent;
    border: 1px solid #3e4451;
    border-radius: 8px;
    resize: none;
    transition: border-color 0.2s;
    overflow: auto;
    white-space: pre;

    &:focus {
      outline: none;
      border-color: @game-primary-color;
      box-shadow: 0 0 0 3px rgba(64, 169, 255, 0.1);
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
      font-size: 13px;
      white-space: pre-wrap;
    }

    &::-webkit-scrollbar {
      width: 10px;
      height: 10px;
    }

    &::-webkit-scrollbar-track {
      background: #21252b;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #4b5263;
      border-radius: 4px;

      &:hover {
        background: #5c6370;
      }
    }

    &::-webkit-scrollbar-corner {
      background: #21252b;
    }
  }
}

// 清空設定確認 Modal 樣式
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  max-width: 480px;
  width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e8e8e8;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
  }
}

.modal-body {
  padding: 24px;

  p {
    margin: 0;
    font-size: 16px;
    line-height: 1.6;
    color: #666;
  }
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e8e8e8;
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  button {
    padding: 8px 20px;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &.cancel-btn {
      background-color: #f5f5f5;
      color: #666;

      &:hover {
        background-color: #e8e8e8;
      }
    }

    &.confirm-btn {
      background-color: @game-primary-color;
      color: white;

      &:hover {
        background-color: darken(@game-primary-color, 10%);
      }

      &.danger-btn {
        background-color: #ff4d4f;

        &:hover {
          background-color: #ff7875;
        }
      }
    }
  }
}
</style>
